import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import photo1 from "../../assets/abbie-1.jpg";
import h1 from "../../assets/home/4c0d9f486aef5998d0fef3b8f6ab8d.jpg";
import h2 from "../../assets/home/IMG_3148.jpg";
import h3 from "../../assets/home/IMG_3151.jpg";
import h4 from "../../assets/home/IMG_3152.jpg";
import h5 from "../../assets/home/IMG_3153.jpg";
import h6 from "../../assets/home/b1fc412a376ddf3deb585afe7fce59.jpg";
import {
  PAGE_FADE_UP,
  PAGE_REVEAL_DELAY,
  PAGE_REVEAL_DURATION,
  PAGE_REVEAL_EASE,
} from "../../utils/pageReveal";

export { photo1 as heroPhoto };

const SIZE_CLS = {
  sm: "w-28 h-28 xl:w-32 xl:h-32",
  md: "w-32 h-32 xl:w-40 xl:h-40",
};

const PHOTO_REVEAL_VARIANTS = {
  hidden: { opacity: 0, y: 24, scale: 0.94 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
  },
};

function SidePhoto({ src, alt = "", size = "md" }) {
  return (
    <motion.div
      className="relative cursor-pointer"
      initial={{ opacity: 0, scale: 0.88 }}
      whileHover={{ opacity: 1, scale: 1.06 }}
      transition={{ type: "spring", stiffness: 280, damping: 22 }}
    >
      <div className="absolute inset-0 rounded-full border border-lightBrown/20 pointer-events-none" />
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        draggable={false}
        className={`rounded-full object-cover shadow-lg ${SIZE_CLS[size]}`}
      />
    </motion.div>
  );
}

export default function HeroSection() {
  const sectionRef = useRef(null);
  const [showSidePhotos, setShowSidePhotos] = useState(false);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });

  const scale = useTransform(scrollYProgress, [0, 1], [1, 0.4]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setShowSidePhotos(true);
    }, 1350);

    return () => window.clearTimeout(timer);
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen flex items-center justify-center px-4 sm:px-8 md:px-12 lg:px-16 py-16 md:py-0"
    >
      {/* ── Side photos — large screens only, scattered asymmetrically ── */}

      {showSidePhotos && (
        <>
          {/* Left side */}
          <motion.div
            variants={PHOTO_REVEAL_VARIANTS}
            initial="hidden"
            animate="visible"
            transition={{
              delay: 0.08,
              duration: PAGE_REVEAL_DURATION,
              ease: PAGE_REVEAL_EASE,
            }}
            className="hidden lg:block absolute left-16 xl:left-24  top-[12%]"
          >
            <SidePhoto src={h1} size="sm" />
          </motion.div>
          <motion.div
            variants={PHOTO_REVEAL_VARIANTS}
            initial="hidden"
            animate="visible"
            transition={{
              delay: 0.14,
              duration: PAGE_REVEAL_DURATION,
              ease: PAGE_REVEAL_EASE,
            }}
            className="hidden lg:block absolute left-36 xl:left-52 top-[40%]"
          >
            <SidePhoto src={h2} size="md" />
          </motion.div>
          <motion.div
            variants={PHOTO_REVEAL_VARIANTS}
            initial="hidden"
            animate="visible"
            transition={{
              delay: 0.2,
              duration: PAGE_REVEAL_DURATION,
              ease: PAGE_REVEAL_EASE,
            }}
            className="hidden lg:block absolute left-20 xl:left-32  top-[68%]"
          >
            <SidePhoto src={h3} size="sm" />
          </motion.div>

          {/* Right side */}
          <motion.div
            variants={PHOTO_REVEAL_VARIANTS}
            initial="hidden"
            animate="visible"
            transition={{
              delay: 0.11,
              duration: PAGE_REVEAL_DURATION,
              ease: PAGE_REVEAL_EASE,
            }}
            className="hidden lg:block absolute right-36 xl:right-52 top-[10%]"
          >
            <SidePhoto src={h4} size="md" />
          </motion.div>
          <motion.div
            variants={PHOTO_REVEAL_VARIANTS}
            initial="hidden"
            animate="visible"
            transition={{
              delay: 0.17,
              duration: PAGE_REVEAL_DURATION,
              ease: PAGE_REVEAL_EASE,
            }}
            className="hidden lg:block absolute right-16 xl:right-24 top-[42%]"
          >
            <SidePhoto src={h5} size="sm" />
          </motion.div>
          <motion.div
            variants={PHOTO_REVEAL_VARIANTS}
            initial="hidden"
            animate="visible"
            transition={{
              delay: 0.23,
              duration: PAGE_REVEAL_DURATION,
              ease: PAGE_REVEAL_EASE,
            }}
            className="hidden lg:block absolute right-36 xl:right-48 top-[70%]"
          >
            <SidePhoto src={h6} size="md" />
          </motion.div>
        </>
      )}

      {/* Center content — unaffected by side photos */}
      <motion.div
        variants={PAGE_FADE_UP}
        initial="hidden"
        animate="visible"
        transition={{
          delay: PAGE_REVEAL_DELAY,
          duration: PAGE_REVEAL_DURATION,
          ease: PAGE_REVEAL_EASE,
        }}
        className="w-full max-w-xs sm:max-w-sm md:max-w-lg lg:max-w-xl xl:max-w-2xl text-center"
      >
        <h1
          className="font-black leading-tight mb-4 sm:mb-6
          text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl"
        >
          Abbie Luo
        </h1>

        <div className="flex justify-center mb-6 sm:mb-8 md:mb-10">
          <div className="h-[3px] bg-lightBrown rounded-full w-28 sm:w-36 md:w-48 lg:w-64" />
        </div>

        {/* Main photo — scales + fades on scroll */}
        <motion.div
          className="flex justify-center mb-6 sm:mb-8 md:mb-10"
          style={{ scale, opacity, transformOrigin: "center top" }}
        >
          <img
            src={photo1}
            alt="Abbie"
            loading="eager"
            decoding="async"
            fetchPriority="high"
            className="object-cover
              w-36 h-44 sm:w-44 sm:h-56
              md:w-52 md:h-64 lg:w-60 lg:h-72 xl:w-64 xl:h-80"
            style={{ borderRadius: "60% 40% 30% 70% / 60% 30% 70% 40%" }}
          />
        </motion.div>

        {/* Scroll hint */}
        <div className="flex justify-center mt-10 lg:mt-12 animate-bounce">
          <svg
            className="w-6 h-6 lg:w-7 lg:h-7 text-pink opacity-60"
            fill="none"
            stroke="currentColor"
            strokeWidth={4}
            strokeLinecap="round"
            strokeLinejoin="round"
            viewBox="0 0 24 24"
          >
            <path d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </motion.div>
    </section>
  );
}
